import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {noop} from '@github-ui/noop'
import {graphql, useFragment} from 'react-relay'

import {QUERY_FIELDS} from '../constants/queries'
import type {CommonItemProps} from '../constants/types'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import type {PullRequestItem$key} from './__generated__/PullRequestItem.graphql'
import {IssueOrPullRequestUnreadIndicator} from './IssueOrPullRequestUnreadIndicator'
import {IssuePullRequestDescription} from './IssuePullRequestDescription'
import {IssuePullRequestStateIcon} from './IssuePullRequestStateIcon'
import {IssuePullRequestTitle} from './IssuePullRequestTitle'
import styles from './PullRequestItem.module.css'
import {LazyPullRequestItemMetadata, type PullRequestItemMetadataProps} from './PullRequestItemMetadata'

// This component represents a single 'basic' pull request displayed in the list
// It styles a given pull request using the ListItem component

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PullRequestItemQuery = graphql`
  fragment PullRequestItem on PullRequest
  @argumentDefinitions(
    labelPageSize: {type: "Int!", defaultValue: 10}
    includeMilestone: {type: "Boolean!", defaultValue: true}
  ) {
    id
    __typename
    repository {
      name
      owner {
        login
      }
    }
    titleHTML
    ...IssuePullRequestTitle @arguments(labelPageSize: $labelPageSize)
    ...IssuePullRequestDescription @arguments(includeMilestone: $includeMilestone)
    ...IssuePullRequestStateIcon
  }
`

export type PullRequestItemProps = {
  itemKey: PullRequestItem$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  showRepository?: boolean
  sortingItemSelected?: string
  as?: React.ElementType
  role?: string
} & Omit<PullRequestItemMetadataProps, 'itemKey'>

export const PullRequestItem = ({
  itemKey,
  listItemMetadata,
  isActive = false,
  isSelected,
  ref,
  href,
  showCommentCount,
  showCommentZeroCount,
  showAssignees,
  showRepository = true,
  onSelect = noop,
  onClick = noop,
  getMetadataHref,
  sortingItemSelected,
  as,
  role,
}: PullRequestItemProps & CommonItemProps) => {
  const data = useFragment(PullRequestItemQuery, itemKey)
  const titleValue = data && data.titleHTML

  const title = (
    <IssuePullRequestTitle
      value={titleValue}
      dataKey={data}
      href={href}
      ref={ref}
      repositoryOwner={data.repository.owner.login}
      repositoryName={data.repository.name}
      onClick={onClick}
      getLabelHref={label => getMetadataHref(QUERY_FIELDS.label, label)}
    />
  )
  const description = (
    <IssuePullRequestDescription
      repositoryOwner={data.repository.owner.login}
      repositoryName={data.repository.name}
      dataKey={data}
      showRepository={showRepository}
      sortingItemSelected={sortingItemSelected}
      getAuthorHref={(login: string) => getMetadataHref('author', login)}
      id={data.id}
      listItemMetadata={listItemMetadata}
    />
  )

  const lazyMetadata = (
    <LazyPullRequestItemMetadata
      pullId={data.id}
      listItemMetadata={listItemMetadata}
      getMetadataHref={getMetadataHref}
      sortingItemSelected={sortingItemSelected}
      showAssignees={showAssignees}
      showCommentCount={showCommentCount}
      showCommentZeroCount={showCommentZeroCount}
    />
  )
  return (
    <ListItem
      key={data.id}
      title={title}
      isActive={isActive}
      isSelected={isSelected}
      onSelect={onSelect}
      metadata={lazyMetadata}
      metadataContainerClassName={styles.ListItem_0}
      role={role || 'listitem'}
      as={as}
    >
      <IssueOrPullRequestUnreadIndicator listItemMetadata={listItemMetadata} issueId={data.id} />
      <ListItemLeadingContent>
        <IssuePullRequestStateIcon id={data.id} dataKey={data} listItemMetadata={listItemMetadata} />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>{description}</ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

PullRequestItem.nodeType = 'pullRequest'
